import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Button,
  Grid,
  CircularProgress,
  Typography,
  Box,
  InputAdornment,
  Divider,
} from '@mui/material';
import { updateEnterpriseProfile } from '../../services/ClientService';

/**
 * Formulário para atualização de perfil de cliente Empresarial (PJ)
 */
export default function EnterpriseProfileForm({ profileData, onSuccess, onError }) {
  // Estado do formulário
  const [formData, setFormData] = useState({
    email: '',
    login_name: '',
    cnpj: '',
    company_name: '',
    trade_name: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    legal_representative_name: '',
    legal_representative_email: '',
  });

  // Estado de validação do formulário
  const [errors, setErrors] = useState({});

  // Estado de loading
  const [loading, setLoading] = useState(false);

  // Inicializar o formulário com os dados do perfil
  useEffect(() => {
    if (profileData) {
      const initialFormData = {
        email: profileData.email || '',
        login_name: profileData.login_name || '',
        cnpj: profileData.cnpj || '',
        company_name: profileData.company_name || '',
        trade_name: profileData.trade_name || '',
        phone_number: profileData.phone_number || '',
        address: profileData.address || '',
        city: profileData.city || '',
        state: profileData.state || '',
        zip_code: profileData.zip_code || '',
        legal_representative_name: profileData.legal_representative_name || '',
        legal_representative_email: profileData.legal_representative_email || '',
      };
      setFormData(initialFormData);
    }
  }, [profileData]);

  // Manipular mudanças no formulário
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Formatação específica para CNPJ (apenas números)
    if (name === 'cnpj') {
      const cnpjDigits = value.replace(/\D/g, '');
      setFormData({ ...formData, cnpj: cnpjDigits.substring(0, 14) });
      return;
    }

    // Formatação específica para CEP (apenas números)
    if (name === 'zip_code') {
      const zipDigits = value.replace(/\D/g, '');
      setFormData({ ...formData, zip_code: zipDigits.substring(0, 8) });
      return;
    }

    // Formatação para telefone (apenas números)
    if (name === 'phone_number') {
      const phoneDigits = value.replace(/\D/g, '');
      setFormData({ ...formData, phone_number: phoneDigits });
      return;
    }

    // Para outros campos, atualiza normalmente
    setFormData({ ...formData, [name]: value });
  };

  // Função de validação
  const validateForm = () => {
    const newErrors = {};

    // Validar email
    if (!formData.email) {
      newErrors.email = 'O email é obrigatório';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    // Validar login_name
    if (!formData.login_name) {
      newErrors.login_name = 'O nome de login é obrigatório';
    } else if (formData.login_name.length < 3) {
      newErrors.login_name = 'O nome de login deve ter pelo menos 3 caracteres';
    }

    // Validar CNPJ
    if (!formData.cnpj) {
      newErrors.cnpj = 'O CNPJ é obrigatório';
    } else if (formData.cnpj.length !== 14) {
      newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    }

    // Validar razão social
    if (!formData.company_name) {
      newErrors.company_name = 'A razão social é obrigatória';
    } else if (formData.company_name.length < 3) {
      newErrors.company_name = 'A razão social deve ter pelo menos 3 caracteres';
    }

    // Validar email do representante legal, se fornecido
    if (
      formData.legal_representative_email &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.legal_representative_email)
    ) {
      newErrors.legal_representative_email = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Formatar CNPJ para exibição
  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '';
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  // Formatar CEP para exibição
  const formatZipCode = (zipCode) => {
    if (!zipCode) return '';
    return zipCode.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  // Formatar telefone para exibição
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    if (phone.length <= 10) {
      // Formato (XX) XXXX-XXXX para telefones fixos
      return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    // Formato (XX) XXXXX-XXXX para celulares
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar formulário
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Obter o ID do cliente do perfil atual
      const clientId = profileData.id;

      // Enviar dados para atualizar o perfil
      const _result = await updateEnterpriseProfile(clientId, formData);

      // Notificar sucesso
      onSuccess('Perfil atualizado com sucesso!');
    } catch (error) {
      // Notificar erro
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Dados da Empresa
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.email}
            helperText={errors.email || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Nome de Login"
            name="login_name"
            value={formData.login_name}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.login_name}
            helperText={errors.login_name || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="CNPJ"
            name="cnpj"
            value={formData.cnpj}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.cnpj}
            helperText={errors.cnpj || ''}
            placeholder="Apenas números"
            inputProps={{ inputMode: 'numeric', maxLength: 14 }}
            InputProps={{
              endAdornment: formData.cnpj && (
                <InputAdornment position="end">
                  <Typography variant="body2" color="textSecondary">
                    {formatCNPJ(formData.cnpj)}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Razão Social"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.company_name}
            helperText={errors.company_name || ''}
            inputProps={{ maxLength: 150 }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Nome Fantasia"
            name="trade_name"
            value={formData.trade_name}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 150 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>
        Representante Legal
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Nome do Representante Legal"
            name="legal_representative_name"
            value={formData.legal_representative_name}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 150 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Email do Representante Legal"
            type="email"
            name="legal_representative_email"
            value={formData.legal_representative_email}
            onChange={handleChange}
            fullWidth
            error={!!errors.legal_representative_email}
            helperText={errors.legal_representative_email || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>
        Informações de Contato
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Telefone"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            fullWidth
            placeholder="Apenas números"
            inputProps={{ inputMode: 'numeric', maxLength: 11 }}
            InputProps={{
              endAdornment: formData.phone_number && (
                <InputAdornment position="end">
                  <Typography variant="body2" color="textSecondary">
                    {formatPhoneNumber(formData.phone_number)}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="CEP"
            name="zip_code"
            value={formData.zip_code}
            onChange={handleChange}
            fullWidth
            placeholder="Apenas números"
            inputProps={{ inputMode: 'numeric', maxLength: 8 }}
            InputProps={{
              endAdornment: formData.zip_code && (
                <InputAdornment position="end">
                  <Typography variant="body2" color="textSecondary">
                    {formatZipCode(formData.zip_code)}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Endereço"
            name="address"
            value={formData.address}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 150 }}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <TextField
            label="Cidade"
            name="city"
            value={formData.city}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Estado"
            name="state"
            value={formData.state}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 2 }}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={loading}
          sx={{ minWidth: 200 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Atualizar Perfil'}
        </Button>
      </Box>
    </form>
  );
}

EnterpriseProfileForm.propTypes = {
  profileData: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
